import gql from 'graphql-tag'
import { graphql, ChildProps } from 'react-apollo'

export type BeamProfile = {
  line: {
    x1: number
    x2: number
    y1: number
    y2: number
  }
}

export type ChartDataItem = {
  name: string
  value: number
  stages?: Stages
}
export type Stages = {
  erection?: StageData
  composite?: StageData
  fire?: StageData
}
export type StageData = {
  bending: GraphData
  shear: ShearGraphData
  deflection?: GraphData
}
export type ShearGraphData = {
  max: number
  min: number
  values: {
    x: number
    min: number
    max: number
  }[]
  minValues: {
    x: number
    value: number
  }[]
  maxValues: {
    x: number
    value: number
  }[]
}
export type GraphData = {
  max: number
  min: number
  values: {
    x: number
    min: number
    max: number
    qMin: number
    qMax: number
    minMax?: Array<number>
  }[]
}
export type ReportData = {
  designValue: number
  resistance: number
  location: string
}

export type JobResult = {
  id: string
  resultId: string
  projectCase: string | null
  numResults: number
  beamType: string
  beamTitle: string

  beamWidth: number
  productionUnit: string
  productionUnitGreen: string
  epdpath: string
  epdpathgreen: string
  chartData: {
    economicEfficiency: ChartDataItem
    ecologicalEfficiency: ChartDataItem
    co2deltabeam: ChartDataItem
    co2deltabeamGreen: ChartDataItem
    co2deltabeamPerArea: ChartDataItem
    co2deltabeamGreenPerArea: ChartDataItem
    sls: ChartDataItem
    uls: ChartDataItem
  } | null
  reportData: any | null // tslint:disable-line no-any
  errorType: string | null
  errorMessage: string | null
  thumbnail: string | null
}

export const ValueFragment = gql`
  fragment ValueParts on ChartStageData {
    min
    max
    values {
      x
      min
      max
      qMin
      qMax
    }
  }
`

export const ShearValueFragment = gql`
  fragment ShearValueParts on ChartStageShearData {
    min
    max
    minValues {
      x
      value
    }
    maxValues {
      x
      value
    }
  }
`

export const StageFragment = gql`
  fragment StageParts on ChartStages {
    fire {
      bending {
        ...ValueParts
      }
      shear {
        ...ShearValueParts
      }
    }
    composite {
      bending {
        ...ValueParts
      }
      shear {
        ...ShearValueParts
      }
      deflection {
        ...ValueParts
      }
    }
    erection {
      bending {
        ...ValueParts
      }
      shear {
        ...ShearValueParts
      }
      deflection {
        ...ValueParts
      }
    }
  }

  ${ValueFragment}
  ${ShearValueFragment}
`

export const ReportValueFragment = gql`
  fragment ReportValueParts on ReportValues {
    designValue
    resistance
    location
  }
`

export const ReportMemberFragment = gql`
  fragment ReportMemberParts on ReportMemberData {
    bending {
      ...ReportValueParts
    }
    shear {
      ...ReportValueParts
    }
  }

  ${ReportValueFragment}
`

export const JobFragment = gql`
  fragment JobResultParts on JobResult {
    id
    beamType
    beamTitle

    resultId
    projectCase
    errorType
    errorMessage

    numResults
    beamWidth
    productionUnit
    productionUnitGreen
    epdpath
    epdpathgreen
    chartData {
      uls {
        value
        name
        stages {
          ...StageParts
        }
      }
      sls {
        value
        name
        stages {
          ...StageParts
        }
      }
      economicEfficiency {
        value
        name
      }
      ecologicalEfficiency {
        value
        name
      }
      co2deltabeam {
        value
        name
      }
      co2deltabeamGreen {
        value
        name
      }
      co2deltabeamPerArea {
        value
        name
      }
      co2deltabeamGreenPerArea {
        value
        name
      }
    }
    reportData {
      strSteelMember {
        ...ReportMemberParts
      }
      strCompositeMember {
        ...ReportMemberParts
      }
      fireCompositeMember {
        ...ReportMemberParts
      }
    }
  }

  ${StageFragment}

  ${ReportMemberFragment}
`

export const JOB_QUERY = gql`
  query GetJobComplete($jobId: String!) {
    jobComplete(jobId: $jobId) {
      ...JobResultParts
    }
  }
  ${JobFragment}
`

export const JOB_QUERY_SUBSCRIPTION = gql`
  subscription GetJobCompleteSubscription($jobId: String!) {
    jobCompleted(jobId: $jobId) {
      ...JobResultParts
    }
  }
  ${JobFragment}
`

export type JobQueryResult = {
  jobComplete: JobResult[]
}

type InputProps = { jobId: string }

export type ResultDataChildProps = ChildProps<InputProps, JobQueryResult>

export const withJobData = graphql<
  JobQueryResult,
  InputProps,
  ResultDataChildProps
>(JOB_QUERY, {
  options: props => ({
    variables: {
      jobId: props.jobId
    }
  }),
  skip: ownProps => !ownProps.jobId
})



// WEBPACK FOOTER //
// ./src/Components/ResultData/resultDataQuery.ts